import { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { HERO } from "@/constants/testIds";
import { ArrowDown, Play } from "lucide-react";

const VIDEOS = [
  {
    mp4: "/videos/scene1.mp4",
    webm: "/videos/scene1.webm",
    poster: "/videos/poster1.jpg",
    label: "Real roads · real drivers",
  },
  {
    mp4: "/videos/scene2.mp4",
    webm: "/videos/scene2.webm",
    poster: "/videos/poster2.jpg",
    label: "Trusted garages · clear quotes",
  },
  {
    mp4: "/videos/scene3.mp4",
    webm: "/videos/scene3.webm",
    poster: "/videos/poster3.jpg",
    label: "Care when it matters",
  },
];

const CAPTIONS = [
  {
    kicker: "The mechanic you can trust",
    title: ["Reliable auto care,", "easier to find."],
    body: "Connect with trusted mechanics and garages, compare options, and book with clearer service information.",
  },
  {
    kicker: "Choice with confidence",
    title: ["Understand the job", "before you pay."],
    body: "AI agents explain symptoms, maintenance intervals, and repair steps in plain language you can share with your garage.",
  },
  {
    kicker: "One connected platform",
    title: ["Your vehicle,", "your digital garage."],
    body: "VIN-locked history, parts fitment, marketplace stock, and support when you need it - from routine service to urgent help.",
  },
];

export const HeroScrollSequence = () => {
  const wrapRef = useRef(null);
  const videoRefs = [useRef(null), useRef(null), useRef(null)];
  const [activeIdx, setActiveIdx] = useState(0);

  const { scrollYProgress } = useScroll({
    target: wrapRef,
    offset: ["start start", "end end"],
  });

  // Opacity transforms for each video
  const op0 = useTransform(scrollYProgress, [0, 0.28, 0.36], [1, 1, 0]);
  const op1 = useTransform(scrollYProgress, [0.28, 0.36, 0.62, 0.7], [0, 1, 1, 0]);
  const op2 = useTransform(scrollYProgress, [0.62, 0.7, 1], [0, 1, 1]);

  // Track active caption based on scroll
  useEffect(() => {
    const unsub = scrollYProgress.on("change", (v) => {
      const idx = v < 0.33 ? 0 : v < 0.66 ? 1 : 2;
      setActiveIdx(idx);
    });
    return () => unsub();
  }, [scrollYProgress]);

  // Force load + autoplay muted (React 19 sometimes doesn't apply `muted` as property; do it imperatively)
  useEffect(() => {
    const timers = [];
    videoRefs.forEach((r, i) => {
      const v = r.current;
      if (!v) return;
      v.muted = true;
      v.defaultMuted = true;
      v.playsInline = true;
      v.loop = true;
      v.setAttribute("muted", "");
      v.setAttribute("playsinline", "");
      // Kick off a load + play cycle
      try { v.load(); } catch (e) {}
      const t = setTimeout(() => {
        v.play().catch(() => {
          // If autoplay is blocked, retry once on next user interaction
          const retry = () => {
            v.play().catch(() => {});
          };
          window.addEventListener("pointerdown", retry, { once: true });
          window.addEventListener("scroll", retry, { once: true });
        });
      }, i * 100);
      timers.push(t);
    });
    return () => timers.forEach(clearTimeout);
  }, []);

  // Debug: track video load state
  useEffect(() => {
    videoRefs.forEach((r, i) => {
      const v = r.current;
      if (!v) return;
      const onErr = (e) => console.warn(`[hero-video-${i}] error`, v.error);
      v.addEventListener("error", onErr);
      return () => v.removeEventListener("error", onErr);
    });
  }, []);

  const opacities = [op0, op1, op2];

  return (
    <section
      ref={wrapRef}
      id="top"
      data-testid={HERO.section}
      className="relative"
      style={{ height: "320vh" }}
    >
      <div className="sticky top-0 h-screen w-full overflow-hidden bg-void">
        {/* Fallback ambient backdrop (visible while videos load) */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_60%,rgba(47,168,215,0.14),transparent_55%),radial-gradient(ellipse_at_80%_30%,rgba(238,242,245,0.06),transparent_50%)]" />
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />

        {/* Video stack */}
        {VIDEOS.map((v, i) => (
          <motion.div
            key={v.mp4}
            style={{ opacity: opacities[i] }}
            className="absolute inset-0"
          >
            <video
              ref={videoRefs[i]}
              className="h-full w-full object-cover"
              poster={v.poster}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
            >
              <source src={v.webm} type="video/webm" />
              <source src={v.mp4} type="video/mp4" />
            </video>
          </motion.div>
        ))}

        {/* Vignette + scrim for text legibility */}
        <div className="absolute inset-0 bg-gradient-to-b from-void/60 via-void/20 to-void/90 pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-r from-void/70 via-transparent to-transparent pointer-events-none" />

        {/* Corner: framing crosshairs */}
        <div className="absolute bottom-8 left-6 lg:left-10 flex items-center gap-3 text-mymech-grey text-sm">
          <span>mymech</span>
          <span className="h-1 w-1 rounded-full bg-mymech-blue" />
          <span>The mechanic you can trust</span>
        </div>

        {/* Text content */}
        <div className="relative z-10 h-full mx-auto max-w-[1400px] px-6 lg:px-10 flex flex-col justify-end pb-24 lg:pb-32">
          {CAPTIONS.map((c, i) => (
            <motion.div
              key={i}
              className="absolute left-6 lg:left-10 right-6 lg:right-10 bottom-24 lg:bottom-32 max-w-2xl"
              initial={false}
              animate={{
                opacity: activeIdx === i ? 1 : 0,
                y: activeIdx === i ? 0 : 30,
              }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
              <span className="chip-brand">{c.kicker}</span>
              <h1 className="mt-6 font-display font-semibold text-white leading-[0.92] text-5xl sm:text-6xl lg:text-7xl xl:text-8xl tracking-tighter">
                {c.title.map((t, j) => (
                  <span key={j} className="block">
                    {t}
                  </span>
                ))}
              </h1>
              <p className="mt-6 max-w-xl text-white/70 text-base sm:text-lg font-light leading-relaxed">
                {c.body}
              </p>

              {i === 0 && (
                <div className="mt-10 flex flex-wrap items-center gap-4">
                  <Link
                    to="/login"
                    data-testid={HERO.ctaPrimary}
                    className="group inline-flex items-center gap-3 h-12 px-6 rounded-brand bg-mymech-blue text-mymech-black text-sm font-semibold hover:bg-sunset transition-all"
                  >
                    Get started
                    <span className="h-1 w-1 bg-void rounded-full group-hover:w-6 transition-all" />
                  </Link>
                  <a
                    href="#how"
                    data-testid={HERO.ctaSecondary}
                    className="group inline-flex items-center gap-3 h-12 px-6 rounded-brand border border-white/20 hover:border-mymech-blue hover:text-mymech-blue text-white/80 text-sm font-medium transition-all"
                  >
                    <Play className="h-3.5 w-3.5" fill="currentColor" />
                    See how it works
                  </a>
                </div>
              )}
            </motion.div>
          ))}
        </div>

        {/* Scroll indicator */}
        <div
          data-testid={HERO.scrollIndicator}
          className="absolute bottom-6 right-6 lg:right-10 flex flex-col items-center gap-2 text-white/40 font-mono-tech text-[10px] uppercase tracking-widest"
        >
          <span>scroll</span>
          <ArrowDown className="h-4 w-4 animate-bounce" />
        </div>

        {/* Progress bar */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-white/10">
          <motion.div
            style={{ scaleX: scrollYProgress, transformOrigin: "left" }}
            className="h-full bg-mclaren"
          />
        </div>
      </div>
    </section>
  );
};

export default HeroScrollSequence;
